import { useCallback, useRef } from 'react';
import { createStore } from '../state/createStore';
import { PlansEntryReason, type PlansEntryReasonValue } from './plansEntryReason';

export interface PlansListMountTelemetry {
    entryReason: PlansEntryReasonValue;
    plansAttemptOccurrence: number;
    isInitialPlansAttempt: boolean;
}

interface PlansListTelemetryState {
    setNextEntryReason: (reason: PlansEntryReasonValue) => void;
    registerPlansListMount: (explicitReason?: PlansEntryReasonValue) => PlansListMountTelemetry;
}

function usePlansListTelemetry(): PlansListTelemetryState {
    const nextReasonRef = useRef<PlansEntryReasonValue | null>(null);
    const occurrenceRef = useRef(0);

    const setNextEntryReason = useCallback((reason: PlansEntryReasonValue) => {
        nextReasonRef.current = reason;
    }, []);

    const registerPlansListMount = useCallback((explicitReason?: PlansEntryReasonValue) => {
        occurrenceRef.current += 1;

        const entryReason =
            explicitReason ??
            nextReasonRef.current ??
            (occurrenceRef.current === 1
                ? PlansEntryReason.ApplicationEntry
                : PlansEntryReason.Unknown);

        nextReasonRef.current = null;

        return {
            entryReason,
            plansAttemptOccurrence: occurrenceRef.current,
            isInitialPlansAttempt: occurrenceRef.current === 1,
        };
    }, []);

    return { setNextEntryReason, registerPlansListMount };
}

export const PlansListTelemetryStore = createStore(usePlansListTelemetry);

export function useRegisterPlansListMount(
    explicitEntryReason?: PlansEntryReasonValue,
): PlansListMountTelemetry {
    const { registerPlansListMount } = PlansListTelemetryStore.useStore();
    const telemetryRef = useRef<PlansListMountTelemetry>();

    telemetryRef.current ??= registerPlansListMount(explicitEntryReason);

    return telemetryRef.current;
}
